import { createContext, useContext, useEffect, useId, useRef, useState } from "react";

import type { AktionsErgebnis, FeldAktion } from "@repo/shared";

/** Was ein Feld braucht, um seine Aktionen auszuführen – stellt der Konfigurator bereit. */
export type KonfiguratorAktionen = {
  /**
   * Führt die Aktion am Feld mit dem aktuellen Stand aus (Werte und Kennung des Konfigurators).
   * Scheitert nie: Ein Protokoll- oder Netzfehler kommt als Stufe „fehler“ zurück.
   */
  ausfuehren(feld: string, aktion: string): Promise<AktionsErgebnis>;
  /** Zählt bei jeder Werteänderung und jeder neuen Konfigurationsantwort hoch – ein Ergebnis gilt nur für seinen Stand. */
  stand: number;
};

export const KonfiguratorAktionenKontext = createContext<KonfiguratorAktionen | null>(null);

/**
 * Die Aktionen eines Felds, rein aus dem Protokoll: je Aktion ein Knopf, darunter
 * das Ergebnis des Servers. Rein lesend – blockiert nichts. Ändert sich der Stand,
 * verfällt das Ergebnis.
 */
export function FeldAktionen({ feld, aktionen }: { feld: string; aktionen: FeldAktion[] }) {
  const kontext = useContext(KonfiguratorAktionenKontext);
  // Ohne Konfigurator gibt es nichts, womit sich eine Aktion ausführen ließe.
  if (!kontext || aktionen.length === 0) return null;

  return (
    <div className="konfi-baustein feld-aktionen">
      {aktionen.map((aktion) => (
        <AktionsKnopf key={aktion.schluessel} feld={feld} aktion={aktion} kontext={kontext} />
      ))}
    </div>
  );
}

function AktionsKnopf({ feld, aktion, kontext }: { feld: string; aktion: FeldAktion; kontext: KonfiguratorAktionen }) {
  const [ergebnis, setErgebnis] = useState<AktionsErgebnis | null>(null);
  const [laedt, setLaedt] = useState(false);
  /** Zählt Anfragen und Standwechsel – nur die Antwort zur letzten Anfrage im selben Stand zählt. */
  const anfrage = useRef(0);
  const hinweisId = useId();
  const { ausfuehren, stand } = kontext;

  useEffect(() => {
    anfrage.current += 1; // eine noch laufende Antwort zum alten Stand verwerfen
    setErgebnis(null);
    setLaedt(false);
  }, [stand]);

  async function pruefen() {
    const nr = ++anfrage.current;
    setLaedt(true);
    const neu = await ausfuehren(feld, aktion.schluessel);
    if (nr !== anfrage.current) return;
    setErgebnis(neu);
    setLaedt(false);
  }

  return (
    <div className="feld-aktion">
      <div className="feld-aktion__kopf">
        <button
          type="button"
          disabled={laedt}
          aria-describedby={aktion.hinweis ? hinweisId : undefined}
          onClick={() => void pruefen()}
        >
          {laedt ? "Prüfe …" : aktion.label}
        </button>
        {aktion.hinweis && (
          <span id={hinweisId} className="muted small">
            {aktion.hinweis}
          </span>
        )}
      </div>
      <div aria-live="polite">
        {ergebnis && (
          <div className={`aktion-ergebnis aktion-ergebnis--${ergebnis.stufe}`}>
            <p>{ergebnis.text}</p>
            {ergebnis.zusatz.map((zeile, i) => (
              <p key={i}>{zeile}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
